import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const STORAGE_KEY = "ff_ai_chat_session";
const STORAGE_OPEN = "ff_ai_chat_open";

/**
 * Floating chat widget for the on-site AI assistant. Bottom-right toggle
 * button opens a compact panel that talks to `/api/ai/ask` and preserves the
 * conversation across page loads using localStorage for the session_id.
 */
export default function AiChatWidget() {
  const [open, setOpen] = useState(() => localStorage.getItem(STORAGE_OPEN) === "1");
  const [messages, setMessages] = useState([]); // {role, content, at}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const sessionId = () => localStorage.getItem(STORAGE_KEY);
  const setSession = (sid) => localStorage.setItem(STORAGE_KEY, sid);

  // Hydrate history when the widget first opens.
  useEffect(() => {
    if (!open || hydrated) return;
    const sid = sessionId();
    if (!sid) { setHydrated(true); return; }
    (async () => {
      try {
        const { data } = await api.get(`/ai/history?session_id=${encodeURIComponent(sid)}`);
        setMessages(data.messages || []);
      } catch { /* soft-fail */ }
      finally { setHydrated(true); }
    })();
  }, [open, hydrated]);

  useEffect(() => { localStorage.setItem(STORAGE_OPEN, open ? "1" : "0"); }, [open]);

  useEffect(() => {
    // Auto-scroll to newest message.
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    // Focus the input when the panel opens.
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: msg, at: now }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/ask", { message: msg, session_id: sessionId() || null });
      if (data.session_id) setSession(data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, at: new Date().toISOString() }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI unavailable");
      setMessages((prev) => [...prev, { role: "assistant", content: "Hit a snag — try again in a sec.", at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const clearChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMessages([]);
    setHydrated(true);
    toast("Chat cleared");
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 shadow-xl transition-all ${
          open ? "bg-black/60 text-[var(--ff-text-dim)]" : "bg-[var(--ff-red)] text-white hover:brightness-110"
        }`}
        data-testid="ai-chat-toggle"
        aria-label={open ? "Close AI chat" : "Open AI chat"}
      >
        {open ? <X size={16} /> : <><Sparkles size={16} /> <span className="font-mono-ui text-xs uppercase tracking-[0.18em]">Ask AI</span></>}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-20 right-5 z-40 w-[min(400px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-6rem))] bg-[var(--ff-surface)] border border-[var(--ff-border)] shadow-2xl flex flex-col" data-testid="ai-chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--ff-border)] bg-black/40">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[var(--ff-red)]" />
              <div>
                <div className="font-mono-ui text-xs uppercase tracking-[0.18em]">Freshy AI</div>
                <div className="text-[10px] text-[var(--ff-text-dim)] font-mono-ui">Ask about shows, schedule, or the site</div>
              </div>
            </div>
            {messages.length > 0 && (
              <button onClick={clearChat} className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] hover:text-[var(--ff-red)]" data-testid="ai-chat-clear">
                Clear
              </button>
            )}
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
            {messages.length === 0 && !sending && (
              <div className="text-center py-8 space-y-3" data-testid="ai-chat-empty">
                <Sparkles className="text-[var(--ff-red)] mx-auto" size={28} />
                <div className="font-display text-lg">How can I help?</div>
                <div className="text-xs text-[var(--ff-text-dim)] space-y-1.5 max-w-[280px] mx-auto">
                  <button onClick={() => setInput("When does Beats Live air?")} className="w-full text-left px-3 py-2 bg-black/40 hover:bg-black/60 transition-colors" data-testid="ai-suggest-1">
                    &ldquo;When does Beats Live air?&rdquo;
                  </button>
                  <button onClick={() => setInput("How do I rate a show?")} className="w-full text-left px-3 py-2 bg-black/40 hover:bg-black/60 transition-colors" data-testid="ai-suggest-2">
                    &ldquo;How do I rate a show?&rdquo;
                  </button>
                  <button onClick={() => setInput("What's on this weekend?")} className="w-full text-left px-3 py-2 bg-black/40 hover:bg-black/60 transition-colors" data-testid="ai-suggest-3">
                    &ldquo;What&apos;s on this weekend?&rdquo;
                  </button>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {sending && (
              <Bubble role="assistant" content={<span className="inline-flex items-center gap-2 text-[var(--ff-text-dim)]"><Loader2 size={12} className="animate-spin" /> Thinking…</span>} pending />
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[var(--ff-border)] bg-black/40">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 2000))}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask me anything…"
                disabled={sending}
                className="flex-1 p-2 bg-black/40 border border-[var(--ff-border)] text-sm focus:border-[var(--ff-red)] focus:outline-none"
                data-testid="ai-chat-input"
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                className="px-3 bg-[var(--ff-red)] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                data-testid="ai-chat-send"
                aria-label="Send message"
              >
                <Send size={14} />
              </button>
            </div>
            <div className="text-[9px] text-[var(--ff-text-dim)] font-mono-ui uppercase tracking-[0.18em] mt-2 text-center">
              Powered by Claude · 24h memory
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ role, content, pending }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`ai-bubble-${role}`}>
      <div
        className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? "bg-[var(--ff-red)]/20 border border-[var(--ff-red)]/40"
            : `bg-black/40 border border-[var(--ff-border)] ${pending ? "opacity-70" : ""}`
        }`}
      >
        {typeof content === "string" ? <MarkdownLite text={content} /> : content}
      </div>
    </div>
  );
}

/** Extremely lightweight markdown renderer — enough for **bold**, `code`, and links. */
function MarkdownLite({ text }) {
  const html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 bg-black/60 text-[var(--ff-red)] font-mono-ui text-xs">$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
             '<a href="$2" target="_blank" rel="noopener" class="text-[var(--ff-red)] underline hover:brightness-125">$1</a>')
    .replace(/\n/g, "<br/>");
  // eslint-disable-next-line react/no-danger
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}
